import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
	NavLink as RouterLink,
	matchPath,
	useLocation,
} from 'react-router-dom';
import { alpha, useTheme, styled } from '@mui/material/styles';
import {
	Box,
	List,
	Collapse,
	ListItemText,
	ListItemIcon,
	ListItemButton,
} from '@mui/material';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';

const ListItemStyle = styled((props) => (
	<ListItemButton
		disableGutters
		{...props}
	/>
))(({ theme }) => ({
	...theme.typography.body2,
	height: 48,
	position: 'relative',
	textTransform: 'capitalize',
	color: theme.palette.text.secondary,
	borderRadius: theme.shape.borderRadius,
}));

const ListItemIconStyle = styled(ListItemIcon)({
	width: 22,
	height: 22,
	color: 'inherit',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

NavItem.propTypes = {
	item: PropTypes.object,
	active: PropTypes.func,
};

function NavItem({ item, active, transactionCount }) {
	const theme = useTheme();

	const isActiveRoot = active(item.path);

	const { title, path, icon, info, children, badge } = item;

	const [open, setOpen] = useState(isActiveRoot);

	const handleOpen = () => {
		setOpen((prev) => !prev);
	};

	useEffect(() => {
		if (item && item?.children) {
			item.children.forEach((resp) => {
				if (active(resp.path)) {
					setOpen(true);
				}
			});
		}
	}, [active, item, item.children, item.hide, transactionCount]);

	const activeRootStyle = {
		color: 'primary.main',
		fontWeight: 'fontWeightBold',
		bgcolor: alpha(
			theme.palette.primary.light,
			theme.palette.action.selectedOpacity
		),
	};

	const activeSubStyle = {
		color: 'text.primary',
		fontWeight: 'fontWeightMedium',
		bgcolor: alpha(
			theme.palette.primary.light,
			theme.palette.action.selectedOpacity
		),
		':hover': {
			bgcolor: alpha(
				theme.palette.primary.light,
				theme.palette.action.selectedOpacity
			),
		},
	};

	if (children) {
		return (
			<>
				<ListItemStyle
					onClick={handleOpen}
					sx={{
						...(isActiveRoot && activeRootStyle),
					}}>
					<ListItemIconStyle>{icon && icon}</ListItemIconStyle>
					<ListItemText
						className={!isActiveRoot ? `font-[500]` : ''}
						disableTypography
						primary={title}
					/>
					<Box sx={{ mx: 2 }}>
						{children.length ? (
							open ? (
								<KeyboardArrowDownOutlinedIcon
									sx={{ color: theme.palette.primary.light }}
								/>
							) : (
								<KeyboardArrowRightOutlinedIcon />
							)
						) : (
							''
						)}
					</Box>
				</ListItemStyle>

				<Collapse
					in={open}
					timeout="auto"
					unmountOnExit>
					<List
						component="div"
						disablePadding>
						{children.map((item) => {
							const { title, path } = item;
							const isActiveSub = active(path);
							return (
								<ListItemStyle
									key={title}
									component={RouterLink}
									to={path}
									sx={{
										...(isActiveSub && activeSubStyle),
									}}>
									<ListItemIconStyle>
										<Box
											component="span"
											sx={{
												width: 4,
												height: 4,
												display: 'flex',
												borderRadius: '50%',
												alignItems: 'center',
												justifyContent: 'center',
												bgcolor: 'text.disabled',
												transition: (theme) =>
													theme.transitions.create('transform'),
												...(isActiveSub && {
													transform: 'scale(2)',
													bgcolor: 'primary.main',
												}),
											}}
										/>
									</ListItemIconStyle>
									<ListItemText
										className={!isActiveRoot ? `font-[500]` : ''}
										disableTypography
										primary={title}
									/>
								</ListItemStyle>
							);
						})}
					</List>
				</Collapse>
			</>
		);
	}

	return (
		<ListItemStyle
			component={RouterLink}
			to={path}
			sx={{
				...(isActiveRoot && activeRootStyle),
			}}>
			<ListItemIconStyle>{icon && icon}</ListItemIconStyle>
			<ListItemText
				className={!isActiveRoot ? `font-[500]` : ''}
				disableTypography
				primary={title}
			/>
			{info && info}
			<Box sx={{ mx: 4 }}>{badge ? badge : null}</Box>
		</ListItemStyle>
	);
}

NavSection.propTypes = {
	navConfig: PropTypes.array,
};

export default function NavSection({ navConfig, ...other }) {
	const { pathname } = useLocation();

	const match = (path) =>
		path ? !!matchPath({ path, end: false }, pathname) : false;

	return (
		<Box {...other}>
			<List
				disablePadding
				sx={{ p: 1 }}>
				{navConfig.map((item) => {
					return !item.hide ? (
						<NavItem
							key={item.title}
							item={item}
							active={match}
						/>
					) : null;
				})}
			</List>
		</Box>
	);
}
